import React, { FC, useRef, useEffect, useCallback } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import {
    ArrowThumbnail3DRef,
    ArrowThumbnail3DProps,
    SceneHander,
} from '@component/thumbnail';
import { AppState } from '@app/react/reducer';
import { KINTEKI, ENTEKI, MAKIWARA, DesignSet } from '@app/react/const/type';
import { useOrderPathParams } from '@app/react/contexts/useOrderPathParams';
import { getCsrfToken } from '@app/react/common/getCsrfToken';
import { isDesignSet } from '@app/react/common/isDesignSet';
import { addCartAction } from '@app/react/action/AddCartAction';
import { showConfirm3DAction } from '@app/react/action/Confirm3DAction';
import { useHistoryGoParts } from '@app/react/contexts/useHistoryGoParts';
import { useMediaMobile } from '@app/react/common/useMedia';
import {
    loadKintekiModelAndTexture,
    loadEntekiModelAndTexture,
    loadMakiwaraModelAndTexture,
} from './loadModel';

const selectId = (state: AppState): number | null => state.customize.id;

const selectBaseId = (state: AppState): number | null => state.customize.baseId;

// eslint-disable-next-line @typescript-eslint/no-empty-interface
interface Props extends Omit<ArrowThumbnail3DProps, 'onAddCart'> { }

export const ArrowThumbnail3DContainer: FC<Props> = (props) => {
    const {
        item,
        onSelect,
    } = props;

    const dispatch = useDispatch();
    const modelHandleRef = useRef<SceneHander>(null);
    const { arrowType, orderType } = useOrderPathParams();
    const goParts = useHistoryGoParts();
    const isMobile = useMediaMobile();

    const id = useSelector(selectId);
    const baseId = useSelector(selectBaseId);

    const onAddCart = useCallback((value) => {
        onSelect?.(value);
        const csrf = getCsrfToken();
        const act = addCartAction.started({
            csrf,
            arrowType,
            orderType,
            parts: value,
        });
        dispatch(act);
    }, [dispatch, onSelect, arrowType, orderType]);

    const onCustom = useCallback((value: DesignSet) => {
        onSelect?.(value);
        if (value.shaft === null) {
            dispatch(showConfirm3DAction({ visible: false })); // mobile only
            goParts('shaft');
        } else {
            dispatch(showConfirm3DAction({ visible: true })); // mobile only
            goParts('preview'); // mobile only
        }
    }, [goParts, dispatch, onSelect]);

    useEffect(() => {
        switch (arrowType) {
            case KINTEKI:
                loadKintekiModelAndTexture(item, modelHandleRef).catch(() => {});
                break;
            case ENTEKI:
                loadEntekiModelAndTexture(item, modelHandleRef).catch(() => {});
                break;
            case MAKIWARA:
                loadMakiwaraModelAndTexture(item, modelHandleRef).catch(() => {});
                break;
            default:
                break;
        }
    }, [item, modelHandleRef, arrowType]);

    return (
        <ArrowThumbnail3DRef
            {...props}
            ref={modelHandleRef}
            onAddCart={onAddCart}
            onCustom={onCustom}
            onSelect={isMobile ? undefined : onSelect}
            checked={isDesignSet(item, { id, baseId })}
        />
    );
};
